import React, { useCallback, useRef } from "react";
import InvoiceDetailsCard from "../../components/invoiceDetailsCard/InvoiceDetailsCard";
import Header from "../../components/dms_common/Header";
import "./DeliveryInvoiceDetails.css";
import FormHeader from "../../components/dms_common/FormHeader";
import {  useBeforeUnload, useNavigate } from "react-router-dom";
import { useState,useEffect } from "react";
import MultiLevelTable from "../../components/multiLevelTable/multiLevelTable";
import { DeliveryInvoiceDetailType, DeliveryInvoiceSkuDetails, LoadoutDetailsType } from "../../types/types";
import { setBatchDetails, setCollectionData, setCollectionDisableEditing, setReturnDetails, setUpdatedInvoiceDetails } from "../../stateManagement/actions/collectionActions";
import { ChannelkartNetworkPut } from "../../services/networkService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { getReturnDetailsColumnsMetaData, invoiceDeliveryStatusMapping, subTableMetaData } from "./deliveryInvoiceMetaData";
import { handlePartialDelivery, enrichUpdatedReturnData, getCreditAmount, getLoadoutReqBody, ristrictForwardNavigation, validateInvoiceDetails } from "../deliveryConfirmation/DeliveryConfirmationService";
import { CreditNote } from "../../components/creditNote/creditNote";
import { setDeliveryStatus } from "../../stateManagement/actions/collectionActions";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CollectionsState } from "../../stateManagement/reducers/collectionReducer";
import { getDataFromLocalDb } from "../../services/dbService";
import { getSalesReturnDetails } from "../deliveryConfirmation/DeliveryConfirmationService";
import { logLoadTrace } from "../../firebase";
import { Loader } from "../../components/loader/Loader";
import InvoicePrint from "./InvoicePrint";
import { createScrollHandler, handleScroll } from "../../components/dms_common/DmsUtils";
import CancelStatusModal from "./CancelStatusModal";
import StatusDropdown from "../../components/dms_common/StatusDropdown";
import OrderStatusCell from "../../components/dms_common/OrderStatusCell";
import { Product } from "../createSecondaryOrder/models";
import { getMergedCatalogueAndRealTimeStockData } from "../stocks/RealTimeStock/RealTimeStockUtils";

interface LocationState {
  invoiceDetails: DeliveryInvoiceDetailType;
  returnDetailsArray: DeliveryInvoiceSkuDetails[];
  loadoutData: LoadoutDetailsType,
  isPartialDeliveryAllowed:boolean

}

const DeliveryInvoiceDetails = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationState = location?.state as LocationState;
  const loadoutData = locationState?.loadoutData;
  const initialInvoiceDetails = locationState?.invoiceDetails;
  const returnDetailsArray = locationState?.returnDetailsArray ?? [];
  const isPartialDeliveryAllowed = locationState?.isPartialDeliveryAllowed;
  const [rows, setRows] = useState<DeliveryInvoiceSkuDetails[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openCreditNote, setOpenCreditNote] = useState(false);
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const collectiondata = useSelector((state: {collections:CollectionsState}) => state?.collections);
  const { updatedInvoiceDetails, deliveryStatus, returnDetails, disableEditing, batchDetails } = useSelector((state: {collections:CollectionsState}) => state?.collections);
  const [loaded,setLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [subTableRowIds, setSubTableRowIds] = useState<string[]>([]);
  const [rowStatuses, setRowStatuses] = useState<Record<string, string>>({});
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [cancelModalLoading, setCancelModalLoading] = useState<boolean>(false);
  const [pendingCancelRowId, setPendingCancelRowId] = useState<string | null>(null);

  const [scrollTop, setScrollTop] = useState<number>(0); 
  const scrollRef = useRef<HTMLDivElement>(null);
  const [allowTableScroll, setAllowTableScroll] = useState<boolean>(false);
  const [dontAllowContainerScroll, setDontContainerScroll] = useState(true);
  
  if (!initialInvoiceDetails) {
    console.log("redirecting from ",window.location.href);
    navigate(-1);
  }

  useBeforeUnload(
    React.useCallback(() => {
        localStorage.deliveryCollectionData = JSON.stringify(collectiondata);
    }, [collectiondata])
  );

  useEffect(()=>{
    if(localStorage.deliveryCollectionData){
      setCollectionData(JSON.parse(localStorage.deliveryCollectionData));
    }
    if(localStorage.getItem("ristrictForwardNavigation")){
      ristrictForwardNavigation(navigate);
      delete localStorage.ristrictForwardNavigation;
    }
    delete localStorage.deliveryCollectionData;
    setLoaded(true);
  },[]);

  useEffect(() => {
    if(!loaded){
      return;
    }
    logLoadTrace(fetchAndSetData,"loadout_delivery_confirmation_invoice_details_load_time");
  }, [loaded]);


  const fetchAndSetData = async()=>{
    if(returnDetails){
      const creditAmount = await getCreditAmount(returnDetails.returnId);
      setTotalCreditAmount(creditAmount);
    }
    if(batchDetails){
      await enrichUpdatedReturnData(returnDetailsArray,batchDetails);
    }
    
    // Process and populate reasonDetails for sub-table
    const processedRows = returnDetailsArray.map(row => {
      const processedRow = processReasonDetails(row);
      const totalQty = row.totalCaseQty + row.totalPieceQty + row.totalOtherQty;
      const soldQty = row.soldCaseQty + row.soldPieceQty + row.soldOtherQty;
      const returnQty = row.returnCaseQty + row.returnPieceQty + row.returnOtherQty;
      
      // Calculate initial status
      const initialStatus = calculateStatus(totalQty, soldQty, returnQty);
      
      return {
        ...processedRow,
        status: initialStatus
      };
    });
    
    // Initialize row statuses
    const initialStatuses: Record<string, string> = {};
    processedRows.forEach(row => {
      initialStatuses[row.id] = row.status;
    });
    setRowStatuses(initialStatuses);
    
    const productsData = await getDataFromLocalDb("Product");
    const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
    const mergedData = getMergedCatalogueAndRealTimeStockData(
        processedRows,
        productsMap
    );
    setRows(mergedData);
  }

  const handleDeliveryStatusChange = (status: string) => {
    if (!disableEditing) {
      setDeliveryStatus(status);
    }
  };

  // Helper function to calculate status based on quantities
  const calculateStatus = (totalQty: number, soldQty: number, returnQty: number): string => {
    if (totalQty === soldQty && returnQty === 0) {
      return "DELIVERED";
    } else if (totalQty === returnQty && soldQty === 0) {
      return "CANCELLED";
    } else if (soldQty > 0 && returnQty > 0) {
      return "PARTIAL DELIVERED";
    }
    return "DELIVERED"; // Default status
  };

  // Function to update quantities and clear reason details based on status selection
  const updateQuantitiesBasedOnStatus = (row: DeliveryInvoiceSkuDetails, newStatus: string, keepReasonDetails = false) => {
    if (newStatus === "DELIVERED" || newStatus === "Delivered") {
      return {
        ...row,
        soldCaseQty: row.totalCaseQty,
        soldPieceQty: row.totalPieceQty,
        soldOtherQty: row.totalOtherQty,
        returnCaseQty: 0,
        returnPieceQty: 0,
        returnOtherQty: 0,
        // Clear reason details when marking as delivered
        reasonDetails: [],
        batchIds: row.batchIds?.map(batch => ({
          ...batch,
          reasonDetails: [] // Clear reasonDetails in each batch
        })) || []
      };
    } else if (newStatus === "CANCELLED" || newStatus === "Cancelled") {
      return {
        ...row,
        soldCaseQty: 0,
        soldPieceQty: 0,
        soldOtherQty: 0,
        returnCaseQty: row.totalCaseQty,
        returnPieceQty: row.totalPieceQty,
        returnOtherQty: row.totalOtherQty,
        // Keep or clear reason details based on keepReasonDetails flag
        reasonDetails: keepReasonDetails ? (row as any).reasonDetails : [],
        batchIds: keepReasonDetails ? row.batchIds : (row.batchIds?.map(batch => ({
          ...batch,
          reasonDetails: [] // Clear reasonDetails in each batch
        })) || [])
      };
    } else if (newStatus === "PARTIAL DELIVERED" || newStatus === "Partially Delivered") {
      // For Partially Delivered: return qty = 0, sold qty = total qty initially
      // Keep existing reason details as user might want to edit them
      return {
        ...row,
      };
    }
    return row;
  };

  // Handle status change for a specific row
  const handleStatusChange = async (rowId: string, newStatus: string) => {
    // If status is being changed to CANCELLED, show modal
    if (newStatus === "CANCELLED") {
      setPendingCancelRowId(rowId);
      setCancelModalOpen(true);
      return;
    }

    // For other status changes, proceed normally
    setRowStatuses(prev => ({ ...prev, [rowId]: newStatus }));
    
    // Find the target row
    const targetRow = rows.find(row => row.id === rowId);
    if (!targetRow) return;
    
    // Update quantities for all status changes
    setRows(prevRows => 
      prevRows.map(row => {
        if (row.id === rowId) {
          const updatedRow = updateQuantitiesBasedOnStatus(row, newStatus);
          return updatedRow;
        }
        return row;
      })
    );

    // Update Redux batchDetails store for all status changes
    if (batchDetails) {
      const updatedBatchDetails = { ...batchDetails };
      
      // Initialize batch code entry if it doesn't exist
      if (!updatedBatchDetails[targetRow.batchCode]) {
        updatedBatchDetails[targetRow.batchCode] = {};
      }

      if (newStatus === "DELIVERED") {
        // Clear reason details for delivered items
        updatedBatchDetails[targetRow.batchCode][targetRow.batchId ?? "unassigned"] = [];
      // } else if (newStatus === "PARTIAL DELIVERED") {
      //   // Keep existing reason details for partially delivered items (user might edit them later)
      //   // Don't clear existing data, just ensure the structure exists
      //   if (!updatedBatchDetails[targetRow.batchCode][targetRow.batchId]) {
      //     updatedBatchDetails[targetRow.batchCode][targetRow.batchId] = [];
      //   }
      }
      
      // Update Redux store
      setBatchDetails(updatedBatchDetails as any);

      // Update returnDetailsArray with the new batchDetails - crucial for validation and review page!
      await enrichUpdatedReturnData(returnDetailsArray, updatedBatchDetails);
    }
  };

  // Helper function to create reason details for cancelled items
  const createCancelReasonDetails = (row: DeliveryInvoiceSkuDetails, saleCondition: string, reason: string) => {
    // For cancelled items, return qty should equal total qty
    const reasonDetail = {
      reason: reason,
      saleCondition: saleCondition,
      caseQty: row.totalCaseQty,
      pieceQty: row.totalPieceQty,
      otherQty: row.totalOtherQty
    };

    // Update row's batchIds with reason details
    const updatedBatchIds = row.batchIds?.map(batch => ({
      ...batch,
      reasonDetails: [reasonDetail]
    })) || [];

    return {
      ...row,
      batchIds: updatedBatchIds,
      reasonDetails: [reasonDetail] // Also add to row level for sub-table display
    };
  };

  // Helper function to process reasonDetails for sub-table
  const processReasonDetails = (row: DeliveryInvoiceSkuDetails) => {
    const reasonDetails: any[] = [];
    
    if (row.batchIds && Array.isArray(row.batchIds)) {
      row.batchIds.forEach(batchDetail => {
        if (batchDetail.reasonDetails && Array.isArray(batchDetail.reasonDetails)) {
          batchDetail.reasonDetails.forEach(reasonDetail => {
            reasonDetails.push({
              reason: reasonDetail.reason,
              saleCondition: reasonDetail.saleCondition || 'N/A',
              returnCaseQty: reasonDetail.caseQty,
              returnPieceQty: reasonDetail.pieceQty,
              returnOtherQty: reasonDetail.otherQty,
              batchId: batchDetail.batchId
            });
          });
        }
      });
    }
    
    return {
      ...row,
      reasonDetails
    };
  };

  // Set delivery status to PARTIAL DELIVERED on component mount
  useEffect(() => {
    if (!disableEditing) {
      setDeliveryStatus("PARTIAL DELIVERED");
    }
  }, [disableEditing]);

  const handleSearchValueChange = useCallback((newValue: string) => {
    newValue = newValue.toLowerCase();
    const filteredData = returnDetailsArray.filter((rowDataObject:DeliveryInvoiceSkuDetails)=>{
      return rowDataObject.skuCode.toLowerCase().includes(newValue) || rowDataObject.skuName.toLowerCase().includes(newValue)
    })
    
    // Process reasonDetails for filtered data
    const processedFilteredRows = filteredData.map(row => {
      const processedRow = processReasonDetails(row);
      const totalQty = row.totalCaseQty + row.totalPieceQty + row.totalOtherQty;
      const soldQty = row.soldCaseQty + row.soldPieceQty + row.soldOtherQty;
      const returnQty = row.returnCaseQty + row.returnPieceQty + row.returnOtherQty;
      
      // Use existing status from state or calculate initial status
      const currentStatus = rowStatuses[row.id] || calculateStatus(totalQty, soldQty, returnQty);
      
      return {
        ...processedRow,
        status: currentStatus
      };
    });
    
    setRows(processedFilteredRows);
  },[]);

  const handleRedirect = async (rowData: DeliveryInvoiceSkuDetails) => {
    // Get the most current data from the state instead of using the parameter
    const currentRowData = rows.find(row => row.id === rowData.id) || rowData;
    
    const productDetails = (await getDataFromLocalDb("Product")).data[0].data;
    const selectedProductDetails = productDetails[currentRowData.batchCode];
    if(!selectedProductDetails){
      openPopup("Error", "Product details not found for this sku");
      return;
    }
    navigate("deliveryInvoiceEdit", {state:{
      rowData: currentRowData, // Pass the current state data, not the original parameter
      productDetails:selectedProductDetails
    }});
  };

  const validateReturnData = ()=>{
    const returnQty = returnDetailsArray.reduce((sum,salesDetails)=>
      salesDetails.returnCaseQty+salesDetails.returnPieceQty+salesDetails.returnOtherQty + sum
    , 0);
    
    if(returnQty == 0){
      openPopup("Error","Log returns for atleast one sku in case of partial delivery.");
      throw new Error("empty returns");
    }

    const soldQty = returnDetailsArray.reduce((sum,salesDetails)=>salesDetails.soldCaseQty+salesDetails.soldPieceQty+salesDetails.soldOtherQty + sum, 0)
    
    if(soldQty == 0){
      openPopup("Error","Cannot mark all items as cancelled in case of partial delivery.");
      throw new Error("cancell order");
    }
    
  }

  const setAmountInData = (returnedData:any[])=>{
    if(returnedData){
      const skuReturnAmountMap:any = {};
      returnedData.forEach((x:any)=>skuReturnAmountMap[x.batchCode] = Math.abs(x.netAmount));
      returnDetailsArray.forEach(row=>{
        if(skuReturnAmountMap[row.batchCode]){
          row.returnAmount = skuReturnAmountMap[row.batchCode];
        }
      })
    }
  }

  const handlePartialSubmit = async() => {
    validateReturnData()
    setIsSubmitting(true);
      setCollectionDisableEditing(true);
      try{
        let response
        if(loadoutData){
          const reqBody = await getLoadoutReqBody(loadoutData, [initialInvoiceDetails.invoiceNumber], "PARTIAL DELIVERED",batchDetails)
          response = await ChannelkartNetworkPut("/v1/loadoutOrchestrator",reqBody);
        }else{
          response = await handlePartialDelivery(returnDetailsArray, initialInvoiceDetails);
        }
         
        if (response.status === 200) {
          const { returnDetailsArray, returnDetailsObject, returnSalesDetails } = await getSalesReturnDetails(initialInvoiceDetails.invoiceNumber);
          if(returnDetailsObject){
            setReturnDetails(returnDetailsObject);
          }
          setAmountInData(returnSalesDetails);
          initialInvoiceDetails.status = "PARTIAL DELIVERED";
          ristrictForwardNavigation(navigate);
        } else {
          console.error("Failed to update delivery status. Please try again.");
          setCollectionDisableEditing(false);
          openPopup("Error","Failed to update delivery status. Please try again.");
        }
      } catch (error) {
        console.error("Error updating delivery status:", error);
        setCollectionDisableEditing(false);
        openPopup("Error","Something went wrong while updating delivery status.");
      } 
      setIsSubmitting(false);
  }

  const openReviewChanges = async () =>{
    validateReturnData();
    setIsSubmitting(true);
    try{
      const reqBody = await getLoadoutReqBody(loadoutData, [initialInvoiceDetails.invoiceNumber], "PARTIAL DELIVERED",batchDetails);
      
      
      const invoiceDetails = await validateInvoiceDetails(initialInvoiceDetails,returnDetailsArray); 
      // const invoiceDetails = await getSalesDataByInvoiceNumber(initialInvoiceDetails.invoiceNumber);
      invoiceDetails.status = "INVOICED";
      invoiceDetails.invoiceNumber = initialInvoiceDetails.invoiceNumber;
      invoiceDetails.invoiceSerialNumber = initialInvoiceDetails.invoiceSerialNumber;
      navigate("review",{
        state:{
          reqBody,
          invoiceDetails
        }
      })
    }catch(e){
      openPopup("Error","Validation failed, please try again")
    }
    setIsSubmitting(false);
  }

  const handleDeliveredSubmit = async (doNavigate?: boolean) => {
    logLoadTrace(async()=>{
      setIsSubmitting(true);
      try{
        let response;
        if(loadoutData){
          const reqBody = await getLoadoutReqBody(loadoutData, [initialInvoiceDetails.invoiceNumber], "DELIVERED",batchDetails)
          response = await ChannelkartNetworkPut("/v1/loadoutOrchestrator",reqBody);
        }else{
          const user = JSON.parse(localStorage.authContext).user;
          const requestBody = {
            "deliveredInvoices": [
              {
                "referenceNumber": initialInvoiceDetails.invoiceNumber,
                "supplier": user.loginId,
                "loginId": user.loginId,
                "outletCode": initialInvoiceDetails.outletCode,
                "status": "DELIVERED"
              }
            ]
          };
          response = await ChannelkartNetworkPut("/v1/sales/delivered", requestBody);
        }
        if (response.status === 200) {
          setUpdatedInvoiceDetails({});
          setCollectionDisableEditing(true);
          if(doNavigate){
            localStorage.setItem("ristrictForwardNavigation", "true");
            navigate(-1);
          }else{
            openPopup("Success","Invoice Delivered Successfully");
            ristrictForwardNavigation(navigate);
          }
        } else {
          console.error("Failed to update delivery status. Please try again.");
          openPopup("Error","Failed to update delivery status. Please try again.");
        }
      } catch (error) {
        console.error("Error updating delivery status:", error);
        openPopup("Error","Something went wrong while updating delivery status.");
      }
      setIsSubmitting(false);
    },"invoice_deliver_submit_time");
  }

  const handleCancelledSubmit = async (doNavigate?: boolean) => {
    logLoadTrace(async()=>{
      setIsSubmitting(true);
      try{
        let response;
        if(loadoutData){
          const reqBody = await getLoadoutReqBody(loadoutData, [initialInvoiceDetails.invoiceNumber], "CANCELLED",batchDetails)
          response = await ChannelkartNetworkPut("/v1/loadoutOrchestrator",reqBody);
        }else{
          const user = JSON.parse(localStorage.authContext).user;
          const requestBody = {
            "cancelledInvoices": [
              {
                "referenceNumber": initialInvoiceDetails.invoiceNumber,
                "supplier": user.loginId,
                "loginId": user.loginId,
                "outletCode": initialInvoiceDetails.outletCode,
                "status": "CANCELLED"
              }
            ]
          };
          response = await ChannelkartNetworkPut("/v1/sales/cancelled", requestBody);
        }
        if (response.status === 200) {
          console.log("Delivery status updated successfully:", response.data);
          setCollectionDisableEditing(true);
          if(doNavigate){
            localStorage.setItem("ristrictForwardNavigation", "true");
            navigate(-1);
          }else{
            openPopup("Success","Invoice Cancelled Successfully");
            ristrictForwardNavigation(navigate);
          }
        } else {
          console.error("Failed to update delivery status. Please try again.");
          openPopup("Error","Failed to update delivery status. Please try again.");
        }
      } catch (error) {
        console.error("Error updating delivery status:", error);
        openPopup("Error","Something went wrong while updating delivery status.");
      }
      setIsSubmitting(false);
    },"invoice_cancel_delivery_submit_time");
  }

  const handleOpenSubTable = (rowId: string) => {
    if (subTableRowIds.includes(rowId)) {
      setSubTableRowIds(subTableRowIds.filter(id => id != rowId));
    } else {
      setSubTableRowIds([...subTableRowIds, rowId]);
    }
  }

  const isSubTableOpen = (rowId: string) => {
    return subTableRowIds.includes(rowId);
  };

  // Handle cancel modal close
  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setPendingCancelRowId(null);
    setCancelModalLoading(false);
  };

  // Handle cancel modal submit
  const handleCancelModalSubmit = async (saleCondition: string, reason: string) => {
    
    if (!pendingCancelRowId) return;

    setCancelModalLoading(true);

    // Find the row being cancelled
    const targetRow = rows.find(row => row.id === pendingCancelRowId);
    if (!targetRow) {
      handleCancelModalClose();
      return;
    }

    // Create updated row with reason details
    const updatedRowWithReasons = createCancelReasonDetails(targetRow, saleCondition, reason);
    const updatedRowWithQuantities = updateQuantitiesBasedOnStatus(updatedRowWithReasons, "CANCELLED", true);
    
    // Process reason details for sub-table display
    const finalUpdatedRow = processReasonDetails(updatedRowWithQuantities);

    // Update row status
    setRowStatuses(prev => ({ ...prev, [pendingCancelRowId]: "CANCELLED" }));

    // Update rows state
    setRows(prevRows => 
      prevRows.map(row => 
        row.id === pendingCancelRowId ? finalUpdatedRow : row
      )
    );

    // Update Redux batchDetails store
    // Initialize batchDetails if it's null/undefined
    const updatedBatchDetails = batchDetails ? { ...batchDetails } : {};
    
    // Initialize batch code entry if it doesn't exist
    if (!updatedBatchDetails[targetRow.batchCode]) {
      updatedBatchDetails[targetRow.batchCode] = {};
    }
    
    // Find the actual batchId from returnDetailsArray to ensure consistency
    const returnDetailMatch = returnDetailsArray.find(item => item.batchCode === targetRow.batchCode);
    const actualBatchId = returnDetailMatch?.batchIds?.[0]?.batchId || targetRow.batchId;
    
    // Set the reason details in Redux store with proper SalesReasonDetails format
    const reasonDetail = {
      reason: reason,
      saleCondition: saleCondition,
      caseQty: targetRow.totalCaseQty,
      pieceQty: targetRow.totalPieceQty,
      otherQty: targetRow.totalOtherQty
    };
    
    
    updatedBatchDetails[targetRow.batchCode][actualBatchId] = [reasonDetail];
    
    dispatch({
      type: "setBatchDetails",
      payload: updatedBatchDetails
    });

    // Update returnDetailsArray with the new batchDetails - this is crucial for validation!
    await enrichUpdatedReturnData(returnDetailsArray, updatedBatchDetails);

    // Close modal
    handleCancelModalClose();
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  
    const handler = createScrollHandler(
      scrollRef,
      isExpanded,
      setAllowTableScroll,
      setDontContainerScroll
    );
  
    el.addEventListener("wheel", handler);
  
    return () => {
      el.removeEventListener("wheel", handler);
    };
  }, [isExpanded, dontAllowContainerScroll]);

  return (
    <div className="delivery_confirmation" onWheel={(e) => handleScroll(e, setIsExpanded, scrollTop)}>
      <FormHeader
        parentLabel={loadoutData ? "Loadout Details" : "Delivery Confirmation"}
        label={"Invoice list"}
        childLabel={loadoutData ? "Invoice Details": undefined}
        label1Click={()=>{
            navigate(-1);
        }}
        goBackToParent={() => {navigate(-2)}}
        searchFunction={handleSearchValueChange}
      />

      <div ref={scrollRef} style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          overflow: (dontAllowContainerScroll) ? "hidden" : "scroll",
          height: "100%",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE 10+
        }}
      >
        <InvoiceDetailsCard
          invoiceDetails={{ ...initialInvoiceDetails, ...updatedInvoiceDetails }}
          deliveryStatus={deliveryStatus}
          invoiceReturnAmount={returnDetails?.returnAmount ?? 0}
          isPartialDeliveryAllowed={isPartialDeliveryAllowed}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
        />
        <div style={{flex: 1, display: "flex", flexDirection: "column", minHeight: 0}}>
      <div className = "delivery-invoice-table-header">
        <Header
          title={"SKU-wise Return Details"}
        />
        <InvoicePrint invoiceNumber={initialInvoiceDetails.invoiceNumber} allowEInvoice={!initialInvoiceDetails.isEInvoiced && initialInvoiceDetails.hasGstin && (!initialInvoiceDetails.salesInfo?.invoiceStatus.includes("INVOICED","CANCELLED","OUT FOR DELIVERY") || (updatedInvoiceDetails ? true : false))}/>
      </div>
      <MultiLevelTable
        rowData={Array.isArray(rows) ? rows : []}
        columnsMetaData={getReturnDetailsColumnsMetaData({
          disableEditing,
          handleRedirect,
          handleOpenSubTable: ["DELIVERED","CANCELLED"].includes(initialInvoiceDetails.salesInfo.invoiceStatus) ? undefined : handleOpenSubTable,
          isSubTableOpen: ["DELIVERED","CANCELLED"].includes(initialInvoiceDetails.salesInfo.invoiceStatus) ? undefined : isSubTableOpen,
          handleStatusChange,
          rowStatuses,
          statusCellElement: (row: any) => {
            return (["DELIVERED","CANCELLED"].includes(initialInvoiceDetails.salesInfo.invoiceStatus) ? 
            <OrderStatusCell
              orderStatusConfig={invoiceDeliveryStatusMapping}
              statusId={row.status}
            /> : 
            <StatusDropdown   
              id={row.id}
              isDisabled={disableEditing}
              invoiceDraftStatus={rowStatuses[row.id]} 
              onStatusChange={handleStatusChange}
              statusConfig={invoiceDeliveryStatusMapping}
            />)
          }
        })}
        subTableRowIds={subTableRowIds}
        subTableMetaData={subTableMetaData}
        footerElement={()=><div className="delivery_invoice_footer">
          { isSubmitting ? <div style={{width:"2rem", marginRight:"4rem"}}><Loader /></div> : <>
            {!disableEditing && <button
                className={`submit-button-delivery`}
                onClick={openReviewChanges}
              >
                <span className="submit-button-delivery-text">
                  Review Changes
                </span>
              </button>
            }
          </>}
        </div>}
      />
      </div>
      </div>
      <CreditNote open={openCreditNote} setOpen={setOpenCreditNote} outletCode={returnDetails?.outletCode ?? ""} returnId={returnDetails?.returnId ?? ""} returnAmount={returnDetails?.returnAmount ?? 0} creditAmount={totalCreditAmount}/>
      
      {/* Cancel Status Modal */}
      <CancelStatusModal
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        onSubmit={handleCancelModalSubmit}
        loading={cancelModalLoading}
      />
    </div>
    
  );
};

export default DeliveryInvoiceDetails;
